import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRole } from "@/contexts/RoleContext";
import { getStudentTests } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, BookOpen, ClipboardList, CheckCircle2, Clock, Star, Layers, XCircle } from "lucide-react";

const STATUS_CONFIG = {
  assigned: {
    label: "Not Started",
    color: "bg-amber-100 text-amber-700 border-amber-200",
    icon: Clock,
  },
  submitted: {
    label: "Submitted",
    color: "bg-sky-100 text-sky-700 border-sky-200",
    icon: CheckCircle2,
  },
  completed: {
    label: "Submitted",
    color: "bg-sky-100 text-sky-700 border-sky-200",
    icon: CheckCircle2,
  },
  abandoned: {
    label: "Abandoned",
    color: "bg-red-100 text-red-700 border-red-200",
    icon: XCircle,
  },
};

export default function StudentTests() {
  const { learner } = useRole();
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!learner?.learner_id) return;
    getStudentTests(learner.learner_id)
      .then((data) => setTests(data.tests || []))
      .catch(() => setTests([]))
      .finally(() => setLoading(false));
  }, [learner?.learner_id]);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="student-tests-page">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate("/home")}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4 transition"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4" /> Back to home
        </button>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-violet-100 text-violet-700 flex items-center justify-center">
            <ClipboardList className="w-6 h-6" />
          </div>
          <div>
            <h1 className="font-display text-3xl font-bold text-slate-800" data-testid="tests-page-title">
              My Tests
            </h1>
            <p className="text-slate-500 text-sm mt-0.5">Tests assigned by your teacher</p>
          </div>
        </div>
      </div>

      {/* Test list */}
      {loading ? (
        <div className="text-center py-20 text-slate-400" data-testid="tests-loading">
          Loading your tests...
        </div>
      ) : tests.length === 0 ? (
        <Card className="rounded-2xl p-12 text-center border border-slate-200 bg-white" data-testid="no-tests">
          <ClipboardList className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 font-medium">No tests assigned yet</p>
          <p className="text-slate-400 text-sm mt-1">Your teacher will assign tests here when ready</p>
        </Card>
      ) : (
        <div className="space-y-4" data-testid="tests-list">
          {tests.map((test) => {
            const cfg = STATUS_CONFIG[test.status] || STATUS_CONFIG.assigned;
            const Icon = cfg.icon;
            const canAttempt = test.status === "assigned";
            const isSubmitted = test.status === "submitted" || test.status === "completed";

            return (
              <Card
                key={test.test_id}
                className="rounded-2xl p-5 border border-slate-200 bg-white hover:shadow-md transition"
                data-testid={`test-card-${test.test_id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${test.test_type === "FM" ? "bg-fuchsia-50 text-fuchsia-600" : "bg-violet-50 text-violet-600"}`}>
                      {test.test_type === "FM" ? <Layers className="w-5 h-5" /> : <BookOpen className="w-5 h-5" />}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-900 truncate">{test.title || test.subject}</h3>
                        {test.test_type === "FM" && (
                          <span className="inline-flex items-center gap-0.5 text-xs font-bold px-1.5 py-0.5 rounded-full bg-fuchsia-100 text-fuchsia-700 flex-shrink-0" data-testid={`fm-badge-${test.test_id}`}>
                            <Layers className="w-2.5 h-2.5" /> FM
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 truncate">{test.chapter}</p>
                      <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                        <span
                          className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${cfg.color}`}
                          data-testid={`test-status-${test.test_id}`}
                        >
                          <Icon className="w-3 h-3" /> {cfg.label}
                        </span>
                        <span className="text-xs text-slate-400">
                          {test.num_questions} question{test.num_questions !== 1 ? "s" : ""}
                          {test.add_marks && test.total_marks ? ` · ${test.total_marks} marks` : ""}
                        </span>
                        {test.assigned_at && (
                          <span className="text-xs text-slate-400">
                            Assigned {new Date(test.assigned_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      {/* Score released */}
                      {test.scores_released && test.score !== null && (
                        <div className="flex items-center gap-1.5 mt-2 text-emerald-700 font-semibold text-sm">
                          <Star className="w-4 h-4 fill-emerald-500 text-emerald-500" />
                          Score: {test.score}{test.total_marks ? `/${test.total_marks}` : ""}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex-shrink-0">
                    {canAttempt ? (
                      <Button
                        onClick={() => navigate(`/tests/${test.test_id}`)}
                        className="bg-violet-600 hover:bg-violet-700 text-white text-sm"
                        data-testid={`start-test-${test.test_id}`}
                      >
                        Start Test
                      </Button>
                    ) : isSubmitted && test.scores_released ? (
                      <Button
                        variant="outline"
                        onClick={() => navigate(`/tests/${test.test_id}`)}
                        className="text-sm"
                        data-testid={`view-results-${test.test_id}`}
                      >
                        View Results
                      </Button>
                    ) : (
                      <span className="text-xs text-slate-400 italic">Awaiting results</span>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
